<script>
import EcButton from '../ui/EcButton.vue'
import EcCollapse from '../ui/EcCollapse.vue'
import EcSpinner from '../ui/EcSpinner.vue'
import ModuleCredentialEditor from './ModuleCredentialEditor.vue'

export default {
  name: 'ModuleChildService',
  components: { EcButton, EcCollapse, EcSpinner, ModuleCredentialEditor },
  props: {
    service: { type: Object, required: true },
    organization: { type: Object, required: true },
    refreshingServiceId: { type: [String, Number], default: null },
  },
  data() {
    return {
      isOpen: false,
      showConfig: false,
      loading: this.refreshingServiceId === this.service.id,
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    statusLabel() {
      if (this.service.is_active) return this.t('ELIXIO_CONNECT.ACTIVE')
      if (this.service.is_deactivated) return this.t('ELIXIO_CONNECT.DEACTIVATED')
      return this.t('ELIXIO_CONNECT.INACTIVE')
    },
    statusClass() {
      if (this.service.is_active) return 'active'
      if (this.service.is_deactivated) return 'deactivated'
      return 'inactive'
    },
  },
  watch: {
    refreshingServiceId(val) {
      this.loading = val === this.service.id
    },
  },
  methods: {
    toggleCollapse() {
      this.isOpen = !this.isOpen
    },
    toggleConfig() {
      this.showConfig = !this.showConfig
    },
    handleFormSubmitted(payload) {
      this.$emit('submitted', payload)
    },
    handleFormCancelled() {
      this.showConfig = false
      this.$emit('closed')
    },
  },
}
</script>

<template>
  <div class="ec-child-service" :class="`ec-child-service--${statusClass}`">
    <!-- Header (clickable) -->
    <div
      class="ec-child-service__header"
      role="button"
      tabindex="0"
      @click="toggleCollapse"
      @keydown.enter.prevent="toggleCollapse"
      @keydown.space.prevent="toggleCollapse"
    >
      <div class="ec-child-service__left">
        <div class="ec-child-service__logo">
          <img
            v-if="service.logo"
            :src="service.logo"
            :alt="service.name"
          />
          <i v-else class="bi bi-box" />
        </div>
        <div class="ec-child-service__info">
          <span class="ec-child-service__name">{{ service.name }}</span>
          <span class="ec-child-service__desc">{{ service.description }}</span>
        </div>
      </div>

      <div class="ec-child-service__right">
        <span
          class="ec-child-service__status-pill"
          :class="`ec-child-service__status-pill--${statusClass}`"
        >
          {{ statusLabel }}
        </span>
        <i class="bi ec-child-service__chevron" :class="isOpen ? 'bi-chevron-up' : 'bi-chevron-down'" />
      </div>
    </div>

    <!-- Collapsible content -->
    <EcCollapse v-model="isOpen">
      <div class="ec-child-service__body">
        <EcSpinner v-if="loading" size="sm" />

        <template v-else>
          <div>
            <!-- Action buttons -->
            <div class="ec-child-service__actions">
              <EcButton
                v-if="service.is_active"
                variant="outline-primary"
                size="sm"
                icon="gear"
                @click="toggleConfig"
              >
                {{ t('ELIXIO_CONNECT.CONFIGURE') }}
              </EcButton>
              <EcButton
                v-else-if="service.is_deactivated"
                variant="outline-success"
                size="sm"
                icon="power"
                @click="toggleConfig"
              >
                {{ t('ELIXIO_CONNECT.REACTIVATE') }}
              </EcButton>
              <EcButton
                v-else
                variant="primary"
                size="sm"
                icon="power"
                @click="toggleConfig"
              >
                {{ t('ELIXIO_CONNECT.ACTIVATE') }}
              </EcButton>
            </div>

            <!-- Config editor -->
            <ModuleCredentialEditor
              v-if="showConfig"
              :service="service"
              :organization="organization"
              @submitted="handleFormSubmitted"
              @cancelled="handleFormCancelled"
            />
          </div>
        </template>
      </div>
    </EcCollapse>
  </div>
</template>
